import Papa from 'papaparse';
import { SCHEDULE_PUBLICATION_URL, SCHEDULE_TIME_ZONE } from '../../config/schedule';

// Parse Google's published tab metadata as text. Never execute workbook scripts.
export function lastPublishedTab(html) {
  const matches = [...html.matchAll(/items\.push\(\{name:\s*("(?:\\.|[^"\\])*")[\s\S]*?\bgid:\s*"(-?\d+)"/g)];
  if (!matches.length) throw new Error('The published workbook’s tabs could not be read.');
  const last = matches[matches.length - 1];
  const name = JSON.parse(last[1].replace(/\\x([0-9a-f]{2})/gi, '\\u00$1'));
  return { name, gid: last[2] };
}

function calendarDate(year, month, day) {
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function startTime(value) {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  if (!match) return null;
  const [month, day, year, hour, minute, second] = match.slice(1).map(value => Number(value || 0));
  const date = calendarDate(year, month, day);
  if (!date || hour > 23 || minute > 59 || Number(second) > 59) return null;
  return { date, minutes: hour * 60 + minute };
}

function endTime(value) {
  const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i.exec(value);
  if (!match) return null;
  let hour = Number(match[1]);
  const minute = Number(match[2]);
  if (minute > 59 || Number(match[3] || 0) > 59 || (match[4] ? hour < 1 || hour > 12 : hour > 23)) return null;
  if (match[4]) hour = hour % 12 + (match[4].toUpperCase() === 'PM' ? 12 : 0);
  return hour * 60 + minute;
}

export function parseSchedule(csv) {
  const parsed = Papa.parse(csv, { delimiter: ',', skipEmptyLines: 'greedy' });
  const headers = (parsed.data[0] || []).map(value => value.trim().replace(/^\uFEFF/, ''));
  const required = ['Class Date & Time', 'End Time', 'Location', 'Class Type', '_Status'];
  if (required.some(name => !headers.includes(name)) || new Set(headers).size !== headers.length) {
    throw new Error('The latest published tab has incompatible schedule columns.');
  }
  const badRows = new Set(parsed.errors.map(error => error.row));
  const column = Object.fromEntries(headers.map((name, index) => [name, index]));
  let skippedRows = 0;
  const sessions = [];
  parsed.data.slice(1).forEach((row, index) => {
    const read = name => String(row[column[name]] || '').trim();
    const start = startTime(read('Class Date & Time'));
    const end = endTime(read('End Time'));
    const status = read('_Status').toUpperCase();
    if (badRows.has(index + 1) || row.length !== headers.length || !start || end === null || end === start.minutes || !read('Location') || !(status === 'ACTIVE' || status.startsWith('CANCELLED'))) {
      skippedRows++;
      return;
    }
    sessions.push({
      id: `${start.date}-${start.minutes}-${index}`,
      date: start.date,
      startMinutes: start.minutes,
      endMinutes: end,
      overnight: end < start.minutes,
      location: read('Location'),
      classType: read('Class Type') || 'Muaythai',
      cancelled: status.startsWith('CANCELLED'),
      cancellationReason: status === 'CANCELLED_NO_ROOM' ? 'No rooms available' : '',
    });
  });
  if (parsed.errors.some(error => error.row === undefined)) throw new Error('The published schedule could not be parsed.');
  sessions.sort((a, b) => a.date.localeCompare(b.date) || a.startMinutes - b.startMinutes);
  return { sessions, skippedRows };
}

export async function fetchSchedule(signal) {
  const workbook = await fetch(SCHEDULE_PUBLICATION_URL, { signal, cache: 'no-store' });
  if (!workbook.ok) throw new Error('The published workbook could not be reached.');
  const tab = lastPublishedTab(await workbook.text());
  const url = new URL(SCHEDULE_PUBLICATION_URL.replace(/\/pubhtml$/, '/pub'));
  url.searchParams.set('output', 'csv');
  url.searchParams.set('gid', tab.gid);
  const response = await fetch(url.toString(), { signal, cache: 'no-store' });
  if (!response.ok) throw new Error('The latest published schedule could not be reached.');
  return { tab, ...parseSchedule(await response.text()), checkedAt: Date.now() };
}

export function vancouverToday(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: SCHEDULE_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

// Date-only arithmetic uses UTC to avoid DST and the visitor's local timezone.
export function shiftDate(date, days) {
  const value = new Date(`${date}T12:00:00Z`);
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

export function mondayOf(date) {
  const day = new Date(`${date}T12:00:00Z`).getUTCDay();
  return shiftDate(date, -(day + 6) % 7);
}

export function formatDate(date, options = {}) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'UTC', month: 'short', day: 'numeric', ...options }).format(new Date(`${date}T12:00:00Z`));
}

export function formatTime(minutes) {
  const hour = Math.floor(minutes / 60);
  return `${hour % 12 || 12}:${String(minutes % 60).padStart(2, '0')} ${hour >= 12 ? 'PM' : 'AM'}`;
}

export function shiftMonth(month, delta) {
  const date = new Date(`${month}-01T12:00:00Z`);
  date.setUTCMonth(date.getUTCMonth() + delta);
  return date.toISOString().slice(0, 7);
}

export function monthDays(month) {
  const first = `${month}-01`;
  const last = shiftDate(`${shiftMonth(month, 1)}-01`, -1);
  const start = mondayOf(first);
  const end = shiftDate(mondayOf(last), 6);
  const days = [];
  for (let date = start; date <= end; date = shiftDate(date, 1)) days.push(date);
  return days;
}
